import React, { useEffect, useRef, useLayoutEffect, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

const LenisContext = createContext({ current: null });

export function useLenisRef() {
  return useContext(LenisContext);
}

export default function LenisProvider({ children }) {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return <LenisContext.Provider value={lenisRef}>{children}</LenisContext.Provider>;
}

// Scrolls to the top on every route (pathname) change.
// - Skips when a hash anchor is present so intentional #section links keep working.
// - Uses the Lenis instance (immediate jump) when available, else native scroll.
export function ScrollToTop() {
  const { pathname } = useLocation();
  const lenisRef = useLenisRef();

  useLayoutEffect(() => {
    if (window.location.hash) return; // preserve anchor-link behaviour
    const lenis = lenisRef?.current;
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, lenisRef]);

  return null;
}
